import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiHome, FiArrowRight, FiPhone, FiMail, FiAlertCircle } = FiIcons;

function NotFound() {
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // In a real implementation, this would perform a site search
      // For now, we'll redirect to the home page with a query parameter
      window.location.href = `/?search=${encodeURIComponent(searchQuery.trim())}`;
    }
  };

  const quickLinks = [
    { name: 'Home', href: '/', description: 'Return to our homepage' },
    { name: 'Our Services', href: '/services', description: 'Learn about our legal services' },
    { name: 'Meet Our Team', href: '/attorneys', description: 'Meet our experienced attorneys' },
    { name: 'Legal Insights', href: '/blog', description: 'Read our latest legal articles' },
    { name: 'Contact Us', href: '/contact', description: 'Get in touch for consultation' },
    { name: 'About Us', href: '/about', description: 'Learn about our firm\'s history' }
  ];

  const popularPages = [
    { name: 'Corporate Law Services', href: '/services#corporate-law' },
    { name: 'Employment Law Services', href: '/services#employment-law' },
    { name: 'Commercial Litigation', href: '/services#commercial-litigation' },
    { name: 'Schedule Consultation', href: '/contact' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-navy-800 to-navy-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gold-600 rounded-full mb-8">
              <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-white" />
            </div>
            <h1 className="font-opensans font-light text-4xl lg:text-6xl mb-6 text-white">
              Page <span className="text-gold-600">Not Found</span>
            </h1>
            <p className="font-opensans text-xl text-gray-200 max-w-2xl mx-auto mb-8">
              We're sorry, but the page you're looking for doesn't exist or has been moved. 
              Let us help you find what you're looking for.
            </p>
            <div className="text-6xl font-bold text-gold-600 mb-4">404</div>
          </motion.div>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center mb-12"
          >
            <h2 className="font-opensans font-bold text-3xl text-navy-800 mb-4">
              Search Our Website
            </h2>
            <p className="font-opensans text-gray-600 mb-8">
              Try searching for the content you were looking for
            </p>
            
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="relative">
                <SafeIcon 
                  icon={FiSearch} 
                  className="absolute left-4 top-4 w-6 h-6 text-gray-400" 
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for legal services, attorneys, or articles..."
                  className="w-full pl-12 pr-32 py-4 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent font-opensans"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-2 bg-gold-500 text-navy-800 px-6 py-2 rounded-lg font-opensans font-semibold hover:bg-gold-600 transition-colors duration-200"
                >
                  Search
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2 className="font-opensans font-bold text-3xl text-navy-800 mb-8 text-center">
              Quick Navigation
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quickLinks.map((link, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                >
                  <Link
                    to={link.href}
                    className="block bg-gray-50 p-6 rounded-lg hover:bg-gold-50 hover:shadow-lg transition-all duration-300 group"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-opensans font-semibold text-lg text-navy-800 group-hover:text-gold-700">
                        {link.name}
                      </h3>
                      <SafeIcon 
                        icon={FiArrowRight} 
                        className="w-5 h-5 text-gray-400 group-hover:text-gold-600 transform group-hover:translate-x-1 transition-all duration-200" 
                      />
                    </div>
                    <p className="font-opensans text-gray-600 text-sm">
                      {link.description}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Popular Pages Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center"
          >
            <h2 className="font-opensans font-bold text-3xl text-navy-800 mb-8">
              Popular Pages
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {popularPages.map((page, index) => (
                <Link
                  key={index}
                  to={page.href}
                  className="block bg-white p-4 rounded-lg hover:bg-gold-50 hover:shadow-md transition-all duration-300 group"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-opensans text-navy-800 group-hover:text-gold-700">
                      {page.name}
                    </span>
                    <SafeIcon 
                      icon={FiArrowRight} 
                      className="w-4 h-4 text-gray-400 group-hover:text-gold-600 transform group-hover:translate-x-1 transition-all duration-200" 
                    />
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-navy-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <h2 className="font-opensans font-bold text-3xl mb-4">
              Still Can't Find What You're Looking For?
            </h2>
            <p className="font-opensans text-xl text-gray-200 mb-8">
              Our team is here to help. Contact us directly and we'll assist you in finding the information you need.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiPhone} className="w-5 h-5 text-gold-600" />
                <span className="font-opensans">+27 11 123 4567</span>
              </div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiMail} className="w-5 h-5 text-gold-600" />
                <span className="font-opensans">law@gnt.co.za</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="bg-gold-500 text-navy-800 px-8 py-4 rounded-lg font-opensans font-semibold hover:bg-gold-600 transition-colors duration-200 inline-flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiHome} className="w-5 h-5" />
                <span>Go to Homepage</span>
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-opensans font-semibold hover:bg-white hover:text-navy-800 transition-colors duration-200"
              >
                Contact Our Team
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Firm Branding Section */}
      <section className="py-12 bg-white border-t">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
          >
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="bg-navy-800 p-2 rounded-lg">
                <svg className="w-8 h-8 text-gold-600" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z"/>
                  <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" fill="none"/>
                </svg>
              </div>
              <div>
                <h3 className="font-opensans font-bold text-xl text-navy-800">
                  Given Ndlovu & Thibela
                </h3>
                <p className="font-opensans text-sm" style={{ color: '#FFB002' }}>Attorneys</p>
              </div>
            </div>
            <p className="font-opensans text-gray-600 text-sm">
              Premier Johannesburg law firm • 25+ years of trusted legal excellence
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default NotFound;